import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useUser } from '../UserContext';
import { logOut } from '../firebase';
import { FaDroplet } from 'react-icons/fa6';
import logo from '../assets/Tremont Garden Logo.png';
import useRequests from '../hooks/useRequests';

const Navbar = () => {
  const { user, loading, refreshUser } = useUser();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { newRequestsCount, unreadMessagesCount } = useRequests(user?.uid);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const handleLogout = async () => {
    try {
      await logOut();
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const notificationCount = newRequestsCount + unreadMessagesCount;

  return (
    <nav className="bg-stone-100 p-4 shadow-md">
      <div className="max-w-[1120px] mx-auto px-4 w-full flex justify-between items-center">
        <Link to="/" className="flex items-center">
          <img src={logo} alt="Tremont Garden Tool Share Logo" className="h-12 w-auto mr-3" />
          <span className="text-stone-700 text-2xl font-bold">Tool Share</span>
        </Link>
        
        {/* Hamburger menu for mobile */}
        <div className="md:hidden">
          <button onClick={toggleMenu} className="text-stone-700 focus:outline-none">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
            </svg>
          </button>
        </div>

        {/* Desktop menu */}
        <div className="hidden md:flex items-center">
          {!loading && (user ? (
            <>
              <Link to="/my-tools" className="text-stone-700 hover:text-stone-950 mr-6 font-semibold">My Tools</Link>
              <Link to="/requests" className="text-stone-700 hover:text-stone-950 mr-6 font-semibold relative">
                Requests
                {notificationCount > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {notificationCount}
                  </span>
                )}
              </Link>
              <div className="relative inline-block text-left group">
                <button className="text-stone-700 hover:text-stone-950 font-semibold">
                  {user.displayName || 'User'} &#x25BC;
                </button>
                <div className="absolute right-0 mt-2 min-w-max bg-white rounded-md shadow-lg z-20 invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-all duration-300 whitespace-nowrap">
                  <Link to="/profile" className="block px-4 py-2 text-stone-700 hover:bg-stone-100">Profile</Link>
                  {user.isAdmin && (
                    <Link to="/admin" className="block px-4 py-2 text-stone-700 hover:bg-stone-100">Admin</Link>
                  )}
                  <Link to="/well-testing" className="block px-4 py-2 text-stone-700 hover:bg-stone-100 flex items-center">
                    <FaDroplet className="text-blue-500 mr-2" />
                    Well Testing
                  </Link>
                  <button onClick={handleLogout} className="block w-full text-left px-4 py-2 text-stone-700 hover:bg-stone-100">Logout</button>
                </div>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="text-stone-700 hover:text-stone-950 mr-4 font-semibold">Login</Link>
              <Link to="/signup" className="bg-white text-stone-700 hover:bg-stone-300 px-4 py-2 rounded-md font-semibold transition duration-300">Sign Up</Link>
            </>
          ))}
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && !loading && (
        <div className="md:hidden mt-4">
          {user ? (
            <>
              <Link to="/my-tools" className="block py-2 text-stone-700 hover:text-stone-950 font-semibold">My Tools</Link>
              <Link to="/requests" className="block py-2 text-stone-700 hover:text-stone-950 font-semibold relative">
                Requests
                {notificationCount > 0 && (
                  <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-1">
                    {notificationCount}
                  </span>
                )}
              </Link>
              <Link to="/profile" className="block py-2 text-stone-700 hover:text-stone-950 font-semibold">Profile</Link>
              {user.isAdmin && (
                <Link to="/admin" className="block py-2 text-stone-700 hover:text-stone-950 font-semibold">Admin</Link>
              )}
              <Link to="/well-testing" className="block py-2 text-stone-700 hover:text-stone-950 font-semibold flex items-center">
                <FaDroplet className="text-blue-500 mr-2" />
                Well Testing
              </Link>
              <button onClick={handleLogout} className="block w-full text-left py-2 text-stone-700 hover:text-stone-950 font-semibold">Logout</button>
            </>
          ) : (
            <>
              <Link to="/login" className="block py-2 text-stone-700 hover:text-stone-950 font-semibold">Login</Link>
              <Link to="/signup" className="block py-2 text-stone-700 hover:text-stone-950 font-semibold">Sign Up</Link>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;
